import DropdownSelector from "@/components/dropdown-selector/DropdownSelector";
import { FC, useEffect, useState } from "react";
import Modal from 'react-modal';
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { DialogButton, DialogContent } from "../components/dialog/Dialog";
import { useDialog } from "../components/dialog/DialogContext";
import TableTab from "../components/tabs/table-tab";
import { COMMAND_STATE, FILTER_COMMAND, HEART_BEAT_COMMAND, MESSAGE_OVERLOAD_ERROR, MONITORING_COMMAND, MONITORING_COMMAND_STATE } from "../constants/command-status";
import Commands from '../constants/commands';
import DeviceStatus, { DEVICE_STATES2 } from "../constants/device-status";
import { HARDWARE_BIOMETRICS } from "../constants/menu-types";
import { EngineSubCmds, HardwareSubCmds, MaintenanceSubCmds } from '../constants/sub-commands';
import { useLocale } from "../contexts/localeContext";
import { TableContextProvider } from "../contexts/table/TableContextProvider";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { useIcaWebsocketContext } from "../contexts/websocket/IcaWebsocketContext";
import { exportLicenseToXMLFormat, getArrayBufferFromBlob } from "../exporters/LicenseHelper";
import BusyUploadingIcon from '../images/Busy-Uploading.svg';
import closeIcon from '../images/close.svg';
import DisabledIcon from '../images/Disabled.svg';
import OfflineIcon from '../images/Offline.svg';
import OnlineIcon from '../images/Online.svg';
import UnknownIcon from '../images/Unknown.svg';
import UnlicensedIcon from '../images/Unlicensed.svg';
import { Biometric } from "../models/Biometric";
import { ButtonTypes } from "../models/ButtonTypes";
import { Category } from "../models/Category";
import BiometricColumns from "../models/ColumnDefinitions/BiometricColumns";
import { ExecuteCommand } from "../models/ExecuteCommand";
import { ListData } from "../models/ListData";
import { ListQuery } from "../models/ListQuery";
import { Lookup } from "../models/Lookup";
import LookupTypes from "../models/LookupTypes";
import BiometricSearch from "../models/search/BiometricSearch";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { TableLegendItem } from "../models/table/TableLegendItem";
import { getExportedBiometricLicense, importBiometricLicense } from "../services/biometricLicenseService";
import { deleteBiometricDevice, getInstalledBiometricDevices } from "../services/biometricService";
import { executeCommand, executeCommandForBiometricDevices } from "../services/executeCommandService";
import { getSiteNamesWithPagination } from "../services/siteService";
import { getErrorMessage } from "../services/utilities/ServiceError";
import { getDeviceStatusWithLicense } from "../services/utilities/StatusBuilder";
import { getDeviceStates, getWsDevice } from "../utilities/WebsocketUtil";

interface Props {
    setBusy: (isBusy: boolean) => void,
    categories: Category[],
    setCategories: (category: Category[]) => void,
    setBusyMessage: (message: string) => void,
    setShowInstallBiometricModal: (open: boolean) => void
}

const BiometricTab: FC<Props> = ({ setBusy, categories, setCategories, setBusyMessage, setShowInstallBiometricModal }) => {
    const commands = {
        EXPORT_UNLOCK_BIOMETRICS: '0',
        IMPORT_UNLOCK_BIOMETRICS: '1',
        ENABLE_DEVICE: '0',
        DISABLE_DEVICE: '1',
        USER_VALIDATION: '2',
        SYNC_SELECTED: '3',
        SYNC_IDEMIA: HardwareSubCmds.Sync_Idemia,
        SYNC_CONTROLID: HardwareSubCmds.Sync_ControlId,
        SYNC_HIK_VISION: HardwareSubCmds.Sync_hikVision,
        SYNC_HID_AMICO: HardwareSubCmds.Sync_hidAmico
    }

    // The device status codes this view is interesting in
    const filterStatusCodes = [
        DEVICE_STATES2.QUEUE_STATE,
        DEVICE_STATES2.USER_ID_LIST,
        DEVICE_STATES2.OFFLINE,
        DEVICE_STATES2.ONLINE_ICMP,
        DEVICE_STATES2.ONLINE_APP,
        DEVICE_STATES2.BUSY,
        DEVICE_STATES2.UPLOADING];

    const { translate } = useLocale();
    const title = translate.byKey(TranslationKeys.biometrics);
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([])
    const itemPath = '/access-control/hardware/biometric/';
    const [isOpen, setIsOpen] = useState<boolean>();
    const [updateTable, setUpdateTable] = useState(() => (updateMethod: (tableData: any[]) => any[]) => { return });
    const [deviceStatusInfo, setDeviceStatusInfo] = useState<{ address: string, statusCode: number }>({
        address: "",
        statusCode: 0,
    })
    const [userCheckState, setUserCheckState] = useState<{ address: string, expected: string, actual: string }>({
        address: "",
        expected: "",
        actual: ""
    })
    const icaWebSocket = useIcaWebsocketContext();
    let heartbeatInterval;
    let filterDiagnosticsRequestTimeOut;
    let filterStatusCodesRequestTimeOut;

    const [deviceQueueInfo, setDeviceQueueInfo] = useState<{ fixedAddress: string, queue: number }>({
        fixedAddress: "",
        queue: 0,
    })

    const unlockBiometricOptions = [
        { value: commands.EXPORT_UNLOCK_BIOMETRICS, label: translate.byKey(TranslationKeys.exportLicense) },
        { value: commands.IMPORT_UNLOCK_BIOMETRICS, label: translate.byKey(TranslationKeys.importLicense) }
    ];
    const commandOptions = [
        { value: commands.ENABLE_DEVICE, label: translate.byKey(TranslationKeys.enable) },
        { value: commands.DISABLE_DEVICE, label: translate.byKey(TranslationKeys.disable) },
        { value: commands.USER_VALIDATION, label: translate.byKey(TranslationKeys.userValidation) },
        { value: commands.SYNC_SELECTED, label: translate.byKey(TranslationKeys.syncSelectedOnly) },
        { value: commands.SYNC_IDEMIA, label: translate.byKey(TranslationKeys.syncIdemiaOnly) },
        { value: commands.SYNC_CONTROLID, label: translate.byKey(TranslationKeys.syncControlId) },
        { value: commands.SYNC_HIK_VISION, label: translate.byKey(TranslationKeys.syncHikVision) },
        { value: commands.SYNC_HID_AMICO, label: translate.byKey(TranslationKeys.syncHidAmico) }
    ];
    const dialog = useDialog();

    useEffect(() => {
        setLookup([{
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: BiometricSearch.find(item => item.accessor === "site")?.searchOnName
        }]);
    }, []);

    useEffect(() => {
        if (icaWebSocket.connectionState) {
            sendFilterDiagnosticsRequest();
            sendfilterStatuCodeRequest();
            sendHeartbeatRequest();
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_DEVICE, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_START);
            icaWebSocket.setOnMessage(onMessage)
        }

        return () => {
            clearInterval(heartbeatInterval);
            clearTimeout(filterDiagnosticsRequestTimeOut);
            clearTimeout(filterStatusCodesRequestTimeOut);
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_DEVICE, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
        }
    }, [icaWebSocket.connectionState]);

    const sendHeartbeatRequest = () => {
        const heartbeatRequest = {
            "command": MONITORING_COMMAND.MONITORING_DEVICE,
            "attributes": {
                "name": HEART_BEAT_COMMAND.HEART_BEAT,
            }
        }

        heartbeatInterval = setInterval(() => {
            icaWebSocket.sendMessage(JSON.stringify(heartbeatRequest));
        }, 20000);
    }

    const sendFilterDiagnosticsRequest = () => {
        // Tell the server which network updates to report on
        const filterDiagnosticsRequest = {
            "command": MONITORING_COMMAND.MONITORING_DEVICE,
            "attributes": {
                "name": FILTER_COMMAND.FILTER,
                "diagnosticsFilters": {
                    "siteIdFilters": "",
                    "includeControllers": false,
                    "includeDoorControllers": false,
                    "includeBiometrics": true,
                    "includeOfflineDevices": true,
                    "includeOnlineDevices": true
                }
            }
        }

        filterDiagnosticsRequestTimeOut = setTimeout(() => {
            icaWebSocket.sendMessage(JSON.stringify(filterDiagnosticsRequest))
        }, 5000)
    }

    const sendfilterStatuCodeRequest = () => {
        // Tell the server to only report on these device status codes.
        const filterStatusCodesRequest = {
            "command": MONITORING_COMMAND.MONITORING_DEVICE,
            "attributes": {
                "name": FILTER_COMMAND.FILTER,
                "statusCodeFilters": {
                    "includeStatusCodes": filterStatusCodes
                }
            }
        }

        filterStatusCodesRequestTimeOut = setTimeout(() => {
            icaWebSocket.sendMessage(JSON.stringify(filterStatusCodesRequest))
        }, 5000)
    }

    useEffect(() => {
        updateTable((tableData) => {
            return updateBiometricState(deviceStatusInfo.address, tableData, deviceStatusInfo.statusCode)
        })
    }, [deviceStatusInfo])

    useEffect(() => {
        updateTable((tableData) => {
            return updateBiometricStateForUserCheck(userCheckState.address, tableData, userCheckState.expected, userCheckState.actual)
        })
    }, [userCheckState])

    useEffect(() => {
        updateTable((tableData) => {
            return updateBiometricQueue(deviceQueueInfo.fixedAddress, deviceQueueInfo.queue, tableData)
        })
    }, [deviceQueueInfo])

    const tableDataUpdate = (updateTableMethod: (updateMethod: (tableData: any[]) => any[]) => void) => {
        setUpdateTable(() => updateTableMethod);
    }

    const onMessage = (data) => {
        if (!data.data.includes(MESSAGE_OVERLOAD_ERROR)) {
            const response = JSON.parse(data.data);

            if (response.command === MONITORING_COMMAND.MONITORING_DEVICE) {
                const deviceStates = getDeviceStates(response);

                if (Array.isArray(deviceStates)) {
                    deviceStates.forEach((deviceState) => {
                        const wsDevice = getWsDevice(deviceState);
                        const address = ((wsDevice.serialAddress) ? wsDevice.serialAddress : (wsDevice.fixedAddress) ? wsDevice.fixedAddress : wsDevice.ipAddress);

                        if (wsDevice) {
                            // Queue count
                            if (wsDevice.fixedAddress) {
                                const queueCount = wsDevice.uploaderQueueCount;
                                setDeviceQueueInfo({
                                    fixedAddress: wsDevice.fixedAddress,
                                    queue: queueCount
                                });
                            }
                            // User ID's
                            else if (wsDevice.statusCode === DEVICE_STATES2.USER_ID_LIST && address) {
                                const usersExpected = wsDevice.keyValues[0];
                                const actualUsers = wsDevice.keyValues[1];
                                setUserCheckState({
                                    actual: actualUsers,
                                    expected: usersExpected,
                                    address: address
                                })
                            }

                            // State updates (online / offline / busy / uploading)
                            const codeConnected = wsDevice.codeConnected;
                            const codeActivity = wsDevice.codeActivity;
                            const statusCode = wsDevice.statusCode;

                            // Online, offline, and unknown states
                            if (address && codeConnected && (codeConnected === DEVICE_STATES2.ONLINE_APP
                                || codeConnected === DEVICE_STATES2.ONLINE_ICMP
                                || codeConnected === DEVICE_STATES2.OFFLINE
                                || codeConnected === DEVICE_STATES2.UNKNOWN)) {
                                setDeviceStatusInfo({
                                    address,
                                    statusCode: codeConnected
                                });
                            }

                            // Busy and uploading states
                            if (address && codeActivity && (codeActivity === DEVICE_STATES2.UPLOADING || codeActivity === DEVICE_STATES2.BUSY)) {
                                setDeviceStatusInfo({
                                    address,
                                    statusCode: codeActivity
                                });
                            }

                            // Unlicensed
                            if (address && statusCode && statusCode === DEVICE_STATES2.UNLICENSED) {
                                setDeviceStatusInfo({
                                    address,
                                    statusCode: statusCode
                                });
                            }
                        }
                    });
                }
            }
        }
    }

    const updateBiometricState = (address: string, biometrics: Biometric[], statusCode: number) => {
        const biometric = biometrics.find((biometric: Biometric) => biometric.baAddress === address);
        if (biometric) {
            const status = getDeviceStatusWithLicense(biometric.license, biometric.enabled, statusCode);
            if (biometric.baAddress === address && biometric.status !== status) {
                const indexOf = biometrics.indexOf(biometric);
                biometrics[indexOf].status = status;
            }
        }
        
        return biometrics;
    }

    const updateBiometricStateForUserCheck = (biometricSerialAddress: string, biometrics: Biometric[], usersExpected: string, actualUsers: string) => {
        return biometrics.map((biometric: Biometric) => {
            if (biometricSerialAddress === biometric.baAddress) {
                const splitUsersExpected = usersExpected.split(':');
                const splitActualUsers = actualUsers.split(':');
                return {
                    ...biometric,
                    templatesReaderVsDB: splitActualUsers[1] + "/" + splitUsersExpected[1],
                }
            }

            return biometric;
        });
    }

    const updateBiometricQueue = (fixedAddress: string, queue: number, biometrics: Biometric[]) => {
        const biometric = biometrics.find((biometric: Biometric) => biometric.baAddress === fixedAddress);
        
        if (biometric && biometric.queue !== queue) {
            const indexOf = biometrics.indexOf(biometric);
            biometrics[indexOf].queue = queue;
        }

        return biometrics;
    }

    const tableLegendItems: TableLegendItem[] = [
        {
            value: DeviceStatus.Unknown.id,
            imageIcon: UnknownIcon,
            label: translate.byKey(DeviceStatus.Unknown.translationKey) ?? DeviceStatus.Unknown.label,
            tooltip: translate.byKey(DeviceStatus.Unknown.translationKey) ?? DeviceStatus.Unknown.label
        },
        {
            value: DeviceStatus.BusyUploading.id,
            imageIcon: BusyUploadingIcon,
            label: translate.byKey(DeviceStatus.BusyUploading.translationKey) ?? DeviceStatus.BusyUploading.label,
            tooltip: translate.byKey(DeviceStatus.BusyUploading.translationKey) ?? DeviceStatus.BusyUploading.label
        },
        {
            value: DeviceStatus.Offline.id,
            imageIcon: OfflineIcon,
            label: translate.byKey(DeviceStatus.Offline.translationKey) ?? DeviceStatus.Offline.label,
            tooltip: translate.byKey(DeviceStatus.Offline.translationKey) ?? DeviceStatus.Offline.label
        },
        {
            value: DeviceStatus.Online.id,
            imageIcon: OnlineIcon,
            label: translate.byKey(DeviceStatus.Online.translationKey) ?? DeviceStatus.Online.label,
            tooltip: translate.byKey(DeviceStatus.Online.translationKey) ?? DeviceStatus.Online.label
        },
        {
            value: DeviceStatus.Disabled.id,
            imageIcon: DisabledIcon,
            label: translate.byKey(DeviceStatus.Disabled.translationKey) ?? DeviceStatus.Disabled.label,
            tooltip: translate.byKey(DeviceStatus.Disabled.translationKey) ?? DeviceStatus.Disabled.label
        },
        {
            value: DeviceStatus.Unlicensed.id,
            imageIcon: UnlicensedIcon,
            label: translate.byKey(DeviceStatus.Unlicensed.translationKey) ?? DeviceStatus.Unlicensed.label,
            tooltip: translate.byKey(DeviceStatus.Unlicensed.translationKey) ?? DeviceStatus.Unlicensed.label,
        }
    ];

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        setBusyMessage(translate.byKey(TranslationKeys.unstallingMessage))
        return new Promise((resolve, reject) => {
            deleteBiometricDevice(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        const newCatergories: Category[] = categories.map((category) => {
                            return category.id === 1 ? { ...category, count: (category?.count ?? 1) - 1 } : category;
                        })
                        setCategories(newCatergories);
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }

    const checkLicenseOption = (licenseOption: string) => {
        if (licenseOption === commands.EXPORT_UNLOCK_BIOMETRICS) {
            exportBiometricLicenses();
        }
        else {
            setIsOpen(true);
        }
    }

    const exportBiometricLicenses = () => {
        getExportedBiometricLicense()
            .then(async (value) => {
                exportLicenseToXMLFormat(value);
            });
    }

    const handleBiometricLicenseChange = async (event) => {

        // Accepts file type: .conf or .lic
        if (event.length > 0 && event[0].name != null && event[0].size != 0 && (event[0].name.indexOf('.lic') !== -1 || event[0].name.indexOf('.conf') !== -1)) {
            const convertedData = Object.values(await getArrayBufferFromBlob(event[0]))
            importBiometricLicense(convertedData)
                .then(async () => {
                    dataRefresh((listParameters?: ListQuery) => getInstalledBiometricDevices(translate, listParameters))
                    toast.success(translate.byKey(TranslationKeys.licenseImportedSuccessfully));
                })
                .catch((e) => {
                    dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(e) })
                })
                .finally(() => {
                    setBusy(false);
                })
        }
        else {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.unsupportedFileType) });
        }
        setIsOpen(false);
    }

    const checkCommandSelected = (commandOption: string) => {
        if ((commandOption === commands.ENABLE_DEVICE || commandOption === commands.DISABLE_DEVICE) && selectedIds.length === 0) {
            const headingText = (commandOption === commands.ENABLE_DEVICE) ? translate.byKey(TranslationKeys.enableDevice) : translate.byKey(TranslationKeys.disableDevice);
            const dialogContent: DialogContent = {
                title: headingText,
                message: translate.byKey(TranslationKeys.enableDisableDeviceMessage),
                buttons: [
                    {
                        id: "btn_enableDisable",
                        onClick: () => {
                            if (commandOption === commands.ENABLE_DEVICE || commandOption === commands.DISABLE_DEVICE) {
                                onCommandSelected(commandOption)
                            }
                        },
                        text: translate.byKey(TranslationKeys.confirm),
                        type: ButtonTypes.BlueRoundedButton
                    },
                    {
                        id: "btn_Cancel",
                        onClick: () => { }, // Empty onClick will just close the dialog
                        text: translate.byKey(TranslationKeys.cancel),
                        type: ButtonTypes.TextButton,
                        className: "justify-center px-4 py-1 text-base font-medium sm:ml-3 sm:w-auto sm:text-sm"
                    }
                ]
            }
            dialog.pushInfo(dialogContent);
        }
        else if (isSyncSpecificDevices(commandOption)) {
            checkSyncOption(commandOption);
        }
        else {
            onCommandSelected(commandOption);
        }
    }

    const onCommandSelected = (commandOption: string) => {
        let command: ExecuteCommand;

        if (commandOption === commands.USER_VALIDATION) {
            command = {
                cmd: Commands.Device.label,
                cmdSubType: HardwareSubCmds.UserId_List,
            }
        }
        else {
            const ENABLE_STATE = 'ENABLE';
            const DISABLE_STATE = 'DISABLE';
            command = {
                cmd: Commands.Device.label,
                cmdSubType: MaintenanceSubCmds.Toggle_State,
                state: commandOption === commands.ENABLE_DEVICE ? ENABLE_STATE : DISABLE_STATE,
                ids: selectedIds.length > 0 ? selectedIds : null,
            }
        }

        executeCommandForBiometricDevices(command)
            .then(() => {
                if (commandOption !== commands.USER_VALIDATION) {
                    dataRefresh((listParameters?: ListQuery) => getInstalledBiometricDevices(translate, listParameters))
                }
                toast.success(translate.byKey(TranslationKeys.commandExecuted));
            })
            .catch(() => {
                if (commandOption === commands.USER_VALIDATION) {
                   toast.warn(translate.byKey(TranslationKeys.userValidatioWarning));
                    return;
                }
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.applicationError) });
            })
    }

    /*
      A helper function which checks if either of the following is true:
      1. Sync selected was chosen, or
      2. Any of the specific brand sync's were chosen. (This list will grow in future)
    */
    const isSyncSpecificDevices = (syncOption: string) => {
        let syncSpecific = false;

        if (syncOption === commands.SYNC_SELECTED || isSyncByBrand(syncOption)) {
            syncSpecific = true;
        }

        return syncSpecific;
    }

    /*
      A helper function which checks if any of the brand specific sync's were selected.
      E.g. Sync Idemia or Sync Control iD. (This list will grow in future)
    */
    const isSyncByBrand = (syncOption: string) => {
        let syncByBrand = false;

        if (syncOption === commands.SYNC_IDEMIA
            || syncOption === commands.SYNC_CONTROLID
            || syncOption === commands.SYNC_HIK_VISION
            || syncOption === commands.SYNC_HID_AMICO) {
            syncByBrand = true;
        }

        return syncByBrand;
    }

    /*
      Based on the sync option selected, get the
      sub command to use. E.g. SYNC_IDEMIA / SYNC_CONTROLID, etc.
    */
    const getSyncCmdForBrand = (syncOption: string) => {
        let syncCmd = HardwareSubCmds.Sync_Idemia;

        if (syncOption === commands.SYNC_CONTROLID) {
            syncCmd = HardwareSubCmds.Sync_ControlId;
        }
        if (syncOption === commands.SYNC_HIK_VISION) {
            syncCmd = HardwareSubCmds.Sync_hikVision;
        }
        if (syncOption === commands.SYNC_HID_AMICO) {
            syncCmd = HardwareSubCmds.Sync_hidAmico;
        }

        return syncCmd;
    }

    const syncBiometricDevice = async (syncOption: string) => {
        const command: ExecuteCommand = {
            cmd: Commands.Engine.label,
            cmdSubType: getSyncCmdForBrand(syncOption),
            ids: (isSyncSpecificDevices(syncOption)) ? selectedIds : null,
        }

        executeCommandForBiometricDevices(command)
            .then(() => {
                toast.success(translate.byKey(TranslationKeys.syncInProgress));
            })
            .catch((error) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) })
            })
            .finally(() => {
            })
    }

    const checkSyncOption = async (syncOption: string) => {
        const deviceSyncStatus = await checkEngineStatus();
        const dialogButtons: DialogButton[] = [
            {
                id: "btn_sync",
                onClick: () => { syncBiometricDevice(syncOption) },
                text: translate.byKey(TranslationKeys.syncDevices),
                type: ButtonTypes.BlueRoundedButton
            },
            {
                id: "btn_Cancel",
                onClick: () => { }, // Empty onClick will just close the dialog
                text: translate.byKey(TranslationKeys.cancel),
                type: ButtonTypes.TextButton,
                className: "justify-center px-4 py-1 text-base font-medium sm:ml-3 sm:w-auto sm:text-sm"
            }
        ];

        if (syncOption === commands.SYNC_SELECTED && selectedIds.length === 0) {
           toast.warn(translate.byKey(TranslationKeys.pleaseSelectBiometricDevice));
        }
        else if (isSyncByBrand(syncOption)) {
            const toastMessage = deviceSyncStatus === true ? translate.byKey(TranslationKeys.syncAllDevicesMessage) + '  ' + translate.byKey(TranslationKeys.engineSelectedDevicesOnly) : translate.byKey(TranslationKeys.syncAllDevicesMessage);
            const dialogContent: DialogContent = {
                title: translate.byKey(TranslationKeys.syncAllDevices),
                message: toastMessage,
                buttons: dialogButtons
            }
            dialog.pushInfo(dialogContent);
        }
        else if (syncOption === commands.SYNC_SELECTED && selectedIds.length > 0 && deviceSyncStatus === true) {
            const dialogContent: DialogContent = {
                title: translate.byKey(TranslationKeys.syncSelectedDevicesOnly),
                message: translate.byKey(TranslationKeys.engineSelectedDevicesOnly),
                buttons: dialogButtons
            }
            dialog.pushInfo(dialogContent);
        }
        else {
            syncBiometricDevice(syncOption);
        }
    }

    const checkEngineStatus = async () => {
        try {
            const command: ExecuteCommand = {
                cmd: Commands.Engine.label,
                cmdSubType: EngineSubCmds.Is_Engine_Started,
                jsonResponse: ""
            }

            const response: any = await executeCommand(command);
            return response.status === COMMAND_STATE.ENGINE_STOPPED ? true : false;
        } catch (error) {
            toast.warn(getErrorMessage(error));
        }
    }

    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))

    const initialiseUserPreferences = (setDetails: (userLoginId, HARDWARE_BIOMETRICS) => void) => {
        setDetails(userLoginId, HARDWARE_BIOMETRICS);
    }
    // <-- end context

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context

    return <TableContextProvider
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        useTable
        usePagination
        useSearch
        useLegend
        useFilter
        useSort
        deleteMethod={onDelete}
        dataMethod={(listParameters?: ListQuery) => getInstalledBiometricDevices(translate, listParameters)}
        legendItems={tableLegendItems}
        tableDataUpdate={tableDataUpdate}
        tableDescriptor={BiometricColumns}
        searchDescriptor={BiometricSearch}
        manualDataRefresh={manualDataRefresh}
        lookup={lookup}>
        <TableTab
            setShowModal={setShowInstallBiometricModal}
            title={title}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            selectRows={true}
            rowPath={itemPath}
            cyName="biometrics">
            <div className="flex flex-row">
                <DropdownSelector className="flex px-4 rounded-full shadow text-white bg-sidemenu-focus text-sm items-center mr-2" buttonClassName="flex px-8 items-center"
                    onSelected={(event) => checkLicenseOption(event.value)}
                    options={unlockBiometricOptions}>
                    {translate.byKey(TranslationKeys.license)}
                </DropdownSelector>
                <DropdownSelector cyName="biometricCommandBtn" className="flex px-4 rounded-full shadow text-white bg-sidemenu-focus text-sm items-center mr-2" buttonClassName="flex px-8 items-center"
                    onSelected={(event) => checkCommandSelected(event.value)}
                    options={commandOptions}>
                    {translate.byKey(TranslationKeys.commands)}
                </DropdownSelector>
            </div>
            <Modal
                isOpen={isOpen}
                ariaHideApp={false}
                overlayClassName="flex w-screen position-float fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
                className="bg-white border-none m-auto shadow select-none outline-none w-content">
                <div className={`w-full p-2 border-b flex flex-row items-center bg-sidemenu-focus text-white justify-between`}>
                    <div className="flex flex-row">
                        <p className="pl-3 font-bold font text-white">{translate.byKey(TranslationKeys.importLicense)}</p>
                    </div>
                    <img src={closeIcon} onClick={() => setIsOpen(false)} className="cursor-pointer mr-2" />
                </div>
                <div className="container m-4">
                    <input
                        type="file"
                        accept=".lic"
                        onChange={(event) => handleBiometricLicenseChange(event.target.files)} />
                </div>
            </Modal>
        </TableTab>
    </TableContextProvider>
}

export default BiometricTab;
